import React, { useEffect, useMemo } from 'react';
import PropTypes from 'prop-types';
import { Typography } from '@material-ui/core';
import { FormattedMessage } from 'react-intl';
import { LinearProgress } from '@mui/material';
import { useQuery } from '@apollo/client';
import { BATCH_STATUS_QUERY } from '~/modules/common/hooks/useBatchStatus';
import { BATCH_QUERY } from '~/modules/common/components/BillingBatchStatus/BillingBatchStatus';
import { BatchState } from '~/types';

export const ProgressContent = ({
  classes,
  labelId,
  batchId,
  onBatchComplete,
  queryType = 'batch_status'
}) => {
  const isBatchStatusQuery = queryType === 'batch_status';
  const query = isBatchStatusQuery ? BATCH_STATUS_QUERY : BATCH_QUERY;

  const { data, stopPolling } = useQuery(query, {
    variables: { batchId },
    fetchPolicy: 'network-only',
    pollInterval: 500
  });

  const batchStatus = isBatchStatusQuery
    ? data?.getBatchStatus || {}
    : data?.getBillingBatchStatus || {};

  const { executionState, overallProgress } = batchStatus;

  const hasProcessed =
    executionState === BatchState.Succeeded ||
    executionState === BatchState.Cancelled ||
    executionState === BatchState.Failed;

  const progressValue = useMemo(
    () =>
      !isNaN(overallProgress?.currentProgress) &&
      !isNaN(overallProgress?.maximumProgress)
        ? (100 * overallProgress?.currentProgress) /
          overallProgress?.maximumProgress
        : 0,
    [overallProgress?.currentProgress, overallProgress?.maximumProgress]
  );

  useEffect(() => {
    if (!hasProcessed) return;

    stopPolling();
    onBatchComplete({ batchId });
  }, [hasProcessed, stopPolling, batchId, onBatchComplete]);

  return (
    <div className={classes.container}>
      <LinearProgress variant="determinate" value={progressValue} />
      <div className={classes.infoMessage}>
        <Typography variant="caption">
          <FormattedMessage id={labelId} />
        </Typography>
      </div>
    </div>
  );
};

ProgressContent.propTypes = {
  classes: PropTypes.object,
  labelId: PropTypes.string,
  batchId: PropTypes.string,
  onBatchComplete: PropTypes.func,
  queryType: PropTypes.oneOf(['batch_status', 'billing_batch_status'])
};

export default ProgressContent;
